import { COLOR } from 'app-v2/design-tokens'
import { Error, Input, Radio, ReactSelect } from 'app/common/forms'
import { Loader } from 'app/common/loader'
import { Recaptcha } from 'app/common/recaptcha'
import { SmallText, Text } from 'app/common/typography'
import { Formik } from 'formik'
import { MutableRefObject, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import type { SatisfactionForm as FormType } from 'shared/types'
import {
    ageCategories,
    orderPickUpType,
    orderTaking,
    orderType
} from 'shared/utils'
import { number, object, string } from 'yup'
import type { MappedRestaurant } from '../types'
import { Grades } from './Grades'
import {
    StyledAgeContainer,
    StyledBoldSmallText,
    StyledBoldText,
    StyledButtonContainer,
    StyledButtonMedium,
    StyledConditions,
    StyledContainer,
    StyledFlameText,
    StyledGradesContainer,
    StyledQuestion
} from './styles'

const { yellow } = oldColors
const { ONSITE, TAKEAWAY, DRIVE, PARKING } = orderPickUpType
const { CLASSIC, CLICK_AND_COLLECT } = orderType
const { APP, COMPTOIR_DRIVE, KIOSK, WEB } = orderTaking
const gradeValues = [1, 2, 3, 4, 5]

type SatisfactionFormProps = {
    restaurants: MappedRestaurant[],
    restaurantFromUrl: MappedRestaurant,
    sendForm: FormType => void,
    error: any
}

export const SatisfactionForm = ({
    sendForm,
    error,
    restaurantFromUrl,
    restaurants
}: SatisfactionFormProps) => {
    const intl = useIntl()
    const [showCaptcha, setShowCaptcha] = useState(false)
    const recaptchaPromiseRef: MutableRefObject<Promise<string>> = useRef()
    const resolveRef: MutableRefObject = useRef()
    const rejectRef: MutableRefObject = useRef()

    const initRecaptchaPromise = () => {
        recaptchaPromiseRef.current = new Promise((resolve, reject) => {
            resolveRef.current = resolve
            rejectRef.current = reject
        })
    }

    if (!recaptchaPromiseRef.current) {
        initRecaptchaPromise()
    }

    const verifyCallback = (recaptchaToken: string) => {
        if (recaptchaToken) {
            resolveRef.current(recaptchaToken)
        } else {
            rejectRef.current('Error getting recaptcha token')
        }
    }

    const formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        let validationShape = {
            age: number()
                .required(mandatory)
                .integer('satisfaction.form.age.invalid')
                .min(1, 'satisfaction.form.age.error')
                .max(99, 'satisfaction.form.age.error'),
            grades: object().shape({
                ambiance: number(),
                cleanliness: number(),
                clarity: number(),
                navigation: number(),
                waitingTime: number(),
                serviceQuality: number(),
                orderAccuracy: number(),
                price: number(),
                seatAvailability: number()
            }),
            pickUpType: string().required(mandatory),
            orderTaking: string().required(mandatory),
            orderType: string().required(mandatory),
            grade: number()
                .required(mandatory)
                .min(1, mandatory)
                .max(5, mandatory),
            restaurant: string().required(mandatory)
        }
        return object().shape(validationShape)
    }

    const handleSubmit = async (values: any, actions: any) => {
        const index = ageCategories.findIndex(c => values.age <= c)
        const codedAge = index >= 0 ? index + 1 : ageCategories.length + 1
        try {
            setShowCaptcha(true)
            const recaptchaToken = await recaptchaPromiseRef.current
            const data = {
                ...values,
                age: codedAge,
                restaurant: values.restaurant.value,
                recaptchaToken
            }
            await sendForm(data)
        } finally {
            setShowCaptcha(false)
            initRecaptchaPromise()
            actions.setSubmitting(false)
        }
    }

    const sortedRestaurants =
        restaurants &&
        restaurants.sort((a, b) => {
            if (a.label < b.label) return -1
            if (a.label > b.label) return 1
            return 0
        })
    const initialValues = {
        age: '',
        grades: {
            ambiance: 0,
            cleanliness: 0,
            clarity: 0,
            navigation: 0,
            waitingTime: 0,
            serviceQuality: 0,
            orderAccuracy: 0,
            price: 0,
            seatAvailability: 0
        },
        restaurant: restaurantFromUrl || '',
        grade: 0,
        orderType: CLASSIC,
        orderTaking: KIOSK,
        pickUpType: ONSITE
    }

    return (
        <StyledContainer>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values, actions) => handleSubmit(values, actions)}
                validationSchema={formSubscription}>
                {({
                    handleSubmit,
                    handleBlur,
                    values,
                    errors,
                    touched,
                    setFieldValue,
                    isSubmitting
                }) => (
                    <Loader loading={!restaurants}>
                        <>
                            <ReactSelect
                                name="restaurant"
                                labelKey="satisfaction.form.restaurant"
                                placeholderKey="satisfaction.form.restaurant.placeholder"
                                value={values.restaurant}
                                options={sortedRestaurants || []}
                                onChange={setFieldValue}
                                onBlur={handleBlur}
                                TextStyle={StyledFlameText}
                                labelColor={COLOR.PRIMARY}
                            />
                            {errors.restaurant && touched.restaurant && (
                                <Error errorKey={errors.restaurant} />
                            )}
                            <StyledQuestion>
                                <StyledBoldText center>
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.global'
                                    })}
                                </StyledBoldText>
                                <StyledBoldSmallText>
                                    <br />
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.grades'
                                    })}
                                </StyledBoldSmallText>
                            </StyledQuestion>
                            <Grades
                                grades={gradeValues}
                                selectedValue={values.grade}
                                handleClick={value =>
                                    setFieldValue('grade', value)
                                }
                            />
                            {errors.grade && touched.grade && (
                                <Error errorKey={errors.grade} />
                            )}
                            <StyledQuestion>
                                <StyledBoldText center>
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.order.type'
                                    })}
                                </StyledBoldText>
                            </StyledQuestion>
                            <Radio
                                labelKey={
                                    'satisfaction.form.order.type.classic'
                                }
                                checked={values.orderType === CLASSIC}
                                onClick={() => {
                                    if (values.orderType !== CLASSIC) {
                                        setFieldValue('orderType', CLASSIC)
                                        setFieldValue('orderTaking', KIOSK)
                                        setFieldValue('pickUpType', ONSITE)
                                    }
                                }}
                            />
                            <Radio
                                labelKey="satisfaction.form.order.type.cc"
                                checked={values.orderType === CLICK_AND_COLLECT}
                                onClick={() => {
                                    if (
                                        values.orderType !== CLICK_AND_COLLECT
                                    ) {
                                        setFieldValue('orderTaking', WEB)
                                        setFieldValue(
                                            'orderType',
                                            CLICK_AND_COLLECT
                                        )
                                        setFieldValue('pickUpType', ONSITE)
                                    }
                                }}
                            />
                            {errors.orderType && touched.orderType && (
                                <Error errorKey={errors.orderType} />
                            )}
                            <StyledQuestion>
                                <StyledBoldText center>
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.order.taking'
                                    })}
                                </StyledBoldText>
                            </StyledQuestion>
                            {values.orderType === CLICK_AND_COLLECT ? (
                                <>
                                    <Radio
                                        labelKey={
                                            'satisfaction.form.order.taking.web'
                                        }
                                        checked={values.orderTaking === WEB}
                                        onClick={() =>
                                            setFieldValue('orderTaking', WEB)
                                        }
                                    />
                                    <Radio
                                        labelKey={
                                            'satisfaction.form.order.taking.app'
                                        }
                                        checked={values.orderTaking === APP}
                                        onClick={() =>
                                            setFieldValue('orderTaking', APP)
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    <Radio
                                        labelKey={
                                            'satisfaction.form.order.taking.kiosk'
                                        }
                                        checked={values.orderTaking === KIOSK}
                                        onClick={() => {
                                            setFieldValue('orderTaking', KIOSK)
                                            if (
                                                values.orderTaking !== KIOSK &&
                                                values.pickUpType === DRIVE
                                            ) {
                                                setFieldValue(
                                                    'pickUpType',
                                                    ONSITE
                                                )
                                            }
                                        }}
                                    />
                                    <Radio
                                        labelKey={
                                            'satisfaction.form.order.taking.comptoir.drive'
                                        }
                                        checked={
                                            values.orderTaking ===
                                            COMPTOIR_DRIVE
                                        }
                                        onClick={() =>
                                            setFieldValue(
                                                'orderTaking',
                                                COMPTOIR_DRIVE
                                            )
                                        }
                                    />
                                </>
                            )}
                            {errors.orderTaking && touched.orderTaking && (
                                <Error errorKey={errors.orderTaking} />
                            )}
                            <StyledQuestion>
                                <StyledBoldText center>
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.collect'
                                    })}
                                </StyledBoldText>
                            </StyledQuestion>
                            <Radio
                                labelKey="satisfaction.form.collect.onsite"
                                checked={values.pickUpType === ONSITE}
                                onClick={() =>
                                    setFieldValue('pickUpType', ONSITE)
                                }
                            />
                            <Radio
                                labelKey={
                                    'clickandcollect.delivery.choice.takeaway'
                                }
                                checked={values.pickUpType === TAKEAWAY}
                                onClick={() =>
                                    setFieldValue('pickUpType', TAKEAWAY)
                                }
                            />
                            {values.orderTaking === COMPTOIR_DRIVE && (
                                <Radio
                                    labelKey={
                                        'clickandcollect.delivery.choice.drive'
                                    }
                                    checked={values.pickUpType === DRIVE}
                                    onClick={() =>
                                        setFieldValue('pickUpType', DRIVE)
                                    }
                                />
                            )}
                            {values.orderType === CLICK_AND_COLLECT && (
                                <Radio
                                    labelKey={
                                        'satisfaction.form.order.taking.pickup.parking'
                                    }
                                    checked={values.pickUpType === PARKING}
                                    onClick={() =>
                                        setFieldValue('pickUpType', PARKING)
                                    }
                                />
                            )}
                            {errors.pickUpType && touched.pickUpType && (
                                <Error errorKey={errors.pickUpType} />
                            )}
                            <StyledQuestion>
                                <StyledBoldText center>
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.questions.title'
                                    })}
                                </StyledBoldText>
                                <StyledBoldSmallText center>
                                    <br />
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.grades'
                                    })}
                                </StyledBoldSmallText>
                            </StyledQuestion>
                            {Object.entries(values.grades).map(
                                ([key, value], index) => {
                                    if (
                                        key === 'navigation' &&
                                        values.orderType === CLASSIC &&
                                        values.orderTaking !== KIOSK
                                    ) {
                                        return null
                                    }
                                    return (
                                        <StyledGradesContainer key={index}>
                                            <Text>
                                                {intl.formatMessage({
                                                    id: `satisfaction.form.grades.${key}`
                                                })}
                                            </Text>
                                            <Grades
                                                grades={gradeValues}
                                                selectedValue={parseInt(
                                                    value,
                                                    10
                                                )}
                                                handleClick={e =>
                                                    setFieldValue(
                                                        `grades.${key}`,
                                                        e
                                                    )
                                                }
                                            />
                                        </StyledGradesContainer>
                                    )
                                }
                            )}
                            <StyledAgeContainer>
                                <Input
                                    name="age"
                                    type="number"
                                    labelKey="satisfaction.form.age"
                                    placeholderKey="satisfaction.form.age.placeholder"
                                    labelColor={COLOR.PRIMARY}
                                    TextStyle={StyledFlameText}
                                    className={
                                        errors.age && touched.age ? 'error' : ''
                                    }
                                />
                                {errors.age && touched.age && (
                                    <Error errorKey={errors.age} />
                                )}
                            </StyledAgeContainer>
                            <StyledConditions>
                                <SmallText center>
                                    {intl.formatMessage({
                                        id: 'satisfaction.form.conditions'
                                    })}
                                </SmallText>
                            </StyledConditions>
                            {showCaptcha && (
                                <Recaptcha
                                    action="WebSatisfactionForm"
                                    verifyCallback={verifyCallback}
                                />
                            )}
                            {error && <Error errorKey="form.error.default" />}
                            <StyledButtonContainer>
                                <StyledButtonMedium
                                    type="submit"
                                    background={yellow}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}>
                                    {intl.formatMessage({
                                        id: 'form.button.send'
                                    })}
                                </StyledButtonMedium>
                            </StyledButtonContainer>
                        </>
                    </Loader>
                )}
            </Formik>
        </StyledContainer>
    )
}
